import axiosAuth from "@/axios-auth";
import { ESTADOS_VINCULO, extrairErroVinculo } from "@/store/modules/conexao_caixa_erros";

// PONTO DE CONCILIAÇÃO COM O BACKEND. Os nomes abaixo são a única suposição de rota
// deste módulo — se o contrato do ConexaoCaixasController usar outros, mude só aqui.
const ROTAS = {
  status:      "/conexao-caixas/status-conexao",
  config:      "/conexao-caixas/config",
  salvarConfig:"/conexao-caixas/save-config",
  gerarCodigo: "/conexao-caixas/gerar-codigo",
  convite:     "/conexao-caixas/gerar-codigo-compartilhamento",
  resgatar:    "/conexao-caixas/resgatar-compartilhamento",
  perfis:      "/conexao-caixas/perfis",
  perfil:      "/conexao-caixas/selecionar-perfil",
  desconectar: "/conexao-caixas/desconectar",
  statusJob:   (jobId) => `/conexao-caixas/status/${jobId}`,
  relatorioTerminal:  "/conexao-caixas/relatorio-terminal",
  relatorioTerminais: "/conexao-caixas/relatorio-terminais",
};

// Polling: a transição aguardando_login → aguardando_perfil não é empurrada pelo backend,
// então a tela pergunta. 5s é o mesmo ritmo de configuracao.vue/login.vue.
export const POLL_INTERVALO_MS = 5000;
export const POLL_MAX_FALHAS   = 5;        // ~25s sem resposta antes de desistir
export const POLL_TETO_MS      = 30 * 60 * 1000; // aba esquecida não fica pedindo pra sempre

// Mesmo texto de ConexaoCaixasController::_mensagemErroWebhook (agil-online).
// Um ciclo de poll sem `mensagem` não pode deixar essa frase presa na tela
// depois que a manutenção termina — mas só ela: outra mensagem de progresso
// que o app tenha posto ali (ex.: "Aguarde, solicitando relatório...") não é
// tocada, porque o backend pode simplesmente não reenviar `mensagem` em todo
// ciclo sem que isso signifique "limpar o progresso".
export const MSG_MANUTENCAO = "Conexão em manutenção pelo suporte — seu relatório roda em seguida.";

const ESTADOS_QUE_POLLAM = ["aguardando_agente", "aguardando_login"];

// Estes já TÊM vínculo: o que falta é terminar de ligar as pontas. Só `sem_vinculo`
// e `revogado` (e um estado que o backend invente) significam integração ausente.
const ESTADOS_COM_VINCULO = ["aguardando_agente", "aguardando_login", "aguardando_perfil", "ativo"];

// Pedir relatório sem agente de pé e logado no portal devolve 400, e o usuário leva
// o erro na cara. Um motivo por causa, na ordem em que ele consegue resolver: o
// programa aberto, o 2FA, o perfil.
// `logado` é gravado pelo dispatcher quando o agente confirma o login — é o "2FA pronto".
// Função à parte, e não um getter chamando o outro, para os dois lerem a mesma regra sem
// duplicá-la e sem um depender do outro estar resolvido.
//
// ATENÇÃO à ordem: perguntar "o vínculo está ativo?" antes das outras causas é o bug
// que já aconteceu. Com o programa fechado o backend rebaixa o estado para
// `aguardando_agente`, e a resposta virava "Conecte o Conexão Caixa" — mandando
// refazer a integração quando bastava abrir o programa.
function motivoBloqueioRelatorio(getters) {
  // O status chega por requisição assíncrona; até ela voltar "sem_vinculo" é só o
  // default do getter, e acusar falta de vínculo aqui é mentira.
  if (!getters.conexaoCaixaStatusUltimaConsulta) return "Verificando o Conexão Caixa…";

  // A aba aparece para toda lotérica com o app e a permissão, inclusive com o
  // módulo desligado — daí este motivo existir em vez de a opção sumir da tela.
  // Só vale quando a config REALMENTE chegou dizendo isso: lê-la exige a permissão
  // de gerenciar, e quem só tem a de relatórios leva 403. Esperar por ela travaria
  // o painel em "verificando"; presumir desligado acusaria o inocente.
  if (getters.conexaoCaixaConfigCarregada && !getters.conexaoCaixaAtivo) {
    return "Ative o Conexão Parceiros Caixa em Parâmetros → Conexão";
  }

  if (ESTADOS_COM_VINCULO.indexOf(getters.conexaoCaixaVinculoEstado) === -1) {
    return "Conecte o Conexão Caixa para baixar relatórios";
  }

  if (!getters.conexaoCaixaAgenteOnline) return "Agente offline — abra o programa no computador da lotérica";
  if (!getters.conexaoCaixaAgenteLogado) return "Aguardando login no portal da Caixa";
  if (!getters.conexaoCaixaVinculoAtivo) return "Escolha o perfil da Caixa em Parâmetros → Conexão";
  return null;
}

const VINCULO_VAZIO = {
  estado: "sem_vinculo",
  perfil: null,
  cpf_mascarado: null,
  download_url: null,
  agente: { conectado: false, logado: false, ultimo_contato: null },
};

const state = {
  // ---- vínculo com o agente Electron ----
  conexaoCaixaVinculo:        null,
  conexaoCaixaVinculoErro:    null,
  conexaoCaixaVinculoLoading: false,
  conexaoCaixaAtivacao:       null,   // { codigo, expira_em, download_url } — some ao desconectar
  conexaoCaixaConvite:        null,   // { codigo, expira_em } — credencial de vida curta, nunca persistida
  conexaoCaixaPerfis:         [],
  conexaoCaixaPerfisLoading:  false,
  conexaoCaixaStatusPolling:      null,
  conexaoCaixaStatusUltimaConsulta: null,
  conexaoCaixaStatusFalhas:       0,
  conexaoCaixaStatusDesdeMs:      null,
  // ---- config do módulo ----
  conexaoCaixaConfig:  {},
  conexaoCaixaConfigCarregada: false,
  conexaoCaixaLoading: false,
  // ---- relatórios (fora do escopo deste plano, mantido como está) ----
  conexaoCaixaTemAcessoRelatorios:  null,
  conexaoCaixaRelatorioJobId:       null,
  conexaoCaixaRelatorioBase:        null,
  conexaoCaixaRelatorioCaixaId:     null,
  conexaoCaixaRelatorioEstado:      "idle",
  conexaoCaixaRelatorioPolling:     null,
  conexaoCaixaRelatorioResultado:   null,
  conexaoCaixaRelatorioErro:        null,
  conexaoCaixaRelatorioLoading:     false,
  conexaoCaixaTerminaisEstado:    "idle",
  conexaoCaixaTerminaisResultado: null,
  conexaoCaixaTerminaisErro:      null,
  conexaoCaixaTerminaisPolling:   null,
  conexaoCaixaTerminaisJobId:     null,
  conexaoCaixaMensagemProgresso:  '',
};

const mutations = {
  SET_CONEXAO_CAIXA_VINCULO         (state, v) { state.conexaoCaixaVinculo        = v; },
  SET_CONEXAO_CAIXA_VINCULO_ERRO    (state, v) { state.conexaoCaixaVinculoErro    = v; },
  SET_CONEXAO_CAIXA_VINCULO_LOADING (state, v) { state.conexaoCaixaVinculoLoading = v; },
  SET_CONEXAO_CAIXA_ATIVACAO        (state, v) { state.conexaoCaixaAtivacao       = v; },
  SET_CONEXAO_CAIXA_CONVITE         (state, v) { state.conexaoCaixaConvite        = v; },
  SET_CONEXAO_CAIXA_PERFIS          (state, v) { state.conexaoCaixaPerfis         = v; },
  SET_CONEXAO_CAIXA_PERFIS_LOADING  (state, v) { state.conexaoCaixaPerfisLoading  = v; },
  SET_CONEXAO_CAIXA_STATUS_POLLING  (state, v) { state.conexaoCaixaStatusPolling  = v; },
  SET_CONEXAO_CAIXA_STATUS_ULTIMA_CONSULTA (state, v) { state.conexaoCaixaStatusUltimaConsulta = v; },
  SET_CONEXAO_CAIXA_STATUS_FALHAS   (state, v) { state.conexaoCaixaStatusFalhas   = v; },
  SET_CONEXAO_CAIXA_STATUS_DESDE_MS (state, v) { state.conexaoCaixaStatusDesdeMs  = v; },
  SET_CONEXAO_CAIXA_CONFIG          (state, v) { state.conexaoCaixaConfig         = v;
                                                state.conexaoCaixaConfigCarregada = true; },
  SET_CONEXAO_CAIXA_LOADING         (state, v) { state.conexaoCaixaLoading        = v; },
  SET_CONEXAO_CAIXA_TEM_ACESSO_RELATORIOS (state, v) { state.conexaoCaixaTemAcessoRelatorios = v; },
  SET_CONEXAO_CAIXA_RELATORIO_JOB_ID    (state, v) { state.conexaoCaixaRelatorioJobId     = v; },
  SET_CONEXAO_CAIXA_RELATORIO_BASE      (state, v) { state.conexaoCaixaRelatorioBase      = v; },
  SET_CONEXAO_CAIXA_RELATORIO_CAIXA_ID  (state, v) { state.conexaoCaixaRelatorioCaixaId   = v; },
  SET_CONEXAO_CAIXA_RELATORIO_ESTADO    (state, v) { state.conexaoCaixaRelatorioEstado    = v; },
  SET_CONEXAO_CAIXA_RELATORIO_POLLING   (state, v) { state.conexaoCaixaRelatorioPolling   = v; },
  SET_CONEXAO_CAIXA_RELATORIO_RESULTADO (state, v) { state.conexaoCaixaRelatorioResultado = v; },
  SET_CONEXAO_CAIXA_RELATORIO_ERRO      (state, v) { state.conexaoCaixaRelatorioErro      = v; },
  SET_CONEXAO_CAIXA_RELATORIO_LOADING   (state, v) { state.conexaoCaixaRelatorioLoading   = v; },
  SET_CONEXAO_CAIXA_TERMINAIS_ESTADO    (state, v) { state.conexaoCaixaTerminaisEstado    = v; },
  SET_CONEXAO_CAIXA_TERMINAIS_RESULTADO (state, v) { state.conexaoCaixaTerminaisResultado = v; },
  SET_CONEXAO_CAIXA_TERMINAIS_ERRO      (state, v) { state.conexaoCaixaTerminaisErro      = v; },
  SET_CONEXAO_CAIXA_TERMINAIS_POLLING   (state, v) { state.conexaoCaixaTerminaisPolling   = v; },
  SET_CONEXAO_CAIXA_TERMINAIS_JOB_ID    (state, v) { state.conexaoCaixaTerminaisJobId     = v; },
  SET_CONEXAO_CAIXA_MENSAGEM_PROGRESSO  (state, v) { state.conexaoCaixaMensagemProgresso  = v; },
  RESET_CONEXAO_CAIXA(state) {
    clearInterval(state.conexaoCaixaStatusPolling);
    state.conexaoCaixaStatusPolling        = null;
    state.conexaoCaixaVinculo              = null;
    state.conexaoCaixaVinculoErro          = null;
    state.conexaoCaixaVinculoLoading       = false;
    state.conexaoCaixaAtivacao             = null;
    state.conexaoCaixaConvite              = null;
    state.conexaoCaixaPerfis               = [];
    state.conexaoCaixaPerfisLoading        = false;
    state.conexaoCaixaStatusUltimaConsulta = null;
    state.conexaoCaixaStatusFalhas         = 0;
    state.conexaoCaixaStatusDesdeMs        = null;
    state.conexaoCaixaTemAcessoRelatorios  = null;
    state.conexaoCaixaMensagemProgresso    = '';
  },
};

const actions = {
  // Traduz o payload do backend para a forma única que os componentes consomem.
  // snake_case unico: o backend agil-online e snake_case em tudo (conciliado 2026-07-26).
  // A UI nunca fala com o dispatcher, so com o agil-online — nao ha camelCase a tolerar.
  // Estado fora da lista vira "desconhecido" — nunca é remapeado para sem_vinculo.

  async fetchStatusConexaoCaixa({ commit }) {
    const res = await axiosAuth.get(ROTAS.status);
    commit("SET_CONEXAO_CAIXA_STATUS_ULTIMA_CONSULTA", new Date().toISOString());

    if (res.error) {
      const erro = extrairErroVinculo(res);
      commit("SET_CONEXAO_CAIXA_VINCULO_ERRO", erro);
      return { ok: false, erro };
    }

    const payload = (res.data && res.data.data) || {};
    const agente  = payload.agente || {};
    const estadoBruto = payload.estado || "sem_vinculo";

    commit("SET_CONEXAO_CAIXA_VINCULO", {
      estado: ESTADOS_VINCULO.indexOf(estadoBruto) === -1 ? "desconhecido" : estadoBruto,
      perfil: payload.perfil || null,
      cpf_mascarado: payload.cpf_mascarado || null,
      download_url:  payload.download_url || null,
      agente: {
        conectado: agente.conectado === true,
        logado:    agente.logado === true,
        ultimo_contato: agente.ultimo_contato || null,
      },
    });
    commit("SET_CONEXAO_CAIXA_VINCULO_ERRO", null);
    commit("SET_CONEXAO_CAIXA_STATUS_FALHAS", 0);
    return { ok: true };
  },

  async gerarCodigoAtivacaoConexaoCaixa({ commit, dispatch }) {
    commit("SET_CONEXAO_CAIXA_VINCULO_LOADING", true);
    commit("SET_CONEXAO_CAIXA_VINCULO_ERRO", null);

    const res = await axiosAuth.post(ROTAS.gerarCodigo, {});
    commit("SET_CONEXAO_CAIXA_VINCULO_LOADING", false);

    if (res.error) {
      const erro = extrairErroVinculo(res);
      commit("SET_CONEXAO_CAIXA_VINCULO_ERRO", erro);
      return { ok: false, erro };
    }

    const payload = (res.data && res.data.data) || {};
    commit("SET_CONEXAO_CAIXA_ATIVACAO", {
      codigo: payload.codigo || null,
      expira_em: payload.expira_em || null,
      download_url: payload.download_url || null,
    });
    await dispatch("fetchStatusConexaoCaixa");
    return { ok: true };
  },

  // C8 devolve so { vinculo_id } — nao roteia pelo corpo, re-hidrata pelo status ao vivo.
  async resgatarCompartilhamentoConexaoCaixa({ commit, dispatch }, codigo) {
    commit("SET_CONEXAO_CAIXA_VINCULO_LOADING", true);
    commit("SET_CONEXAO_CAIXA_VINCULO_ERRO", null);

    const res = await axiosAuth.post(ROTAS.resgatar, { codigo: (codigo || "").trim() });
    commit("SET_CONEXAO_CAIXA_VINCULO_LOADING", false);

    if (res.error) {
      const erro = extrairErroVinculo(res);
      commit("SET_CONEXAO_CAIXA_VINCULO_ERRO", erro);
      return { ok: false, erro };
    }

    await dispatch("fetchStatusConexaoCaixa");
    return { ok: true };
  },

  async fetchConexaoCaixaPerfis({ commit }) {
    commit("SET_CONEXAO_CAIXA_PERFIS_LOADING", true);
    commit("SET_CONEXAO_CAIXA_VINCULO_ERRO", null);

    const res = await axiosAuth.get(ROTAS.perfis);
    commit("SET_CONEXAO_CAIXA_PERFIS_LOADING", false);

    if (res.error) {
      const erro = extrairErroVinculo(res);
      commit("SET_CONEXAO_CAIXA_PERFIS", []);
      commit("SET_CONEXAO_CAIXA_VINCULO_ERRO", erro);
      return { ok: false, erro };
    }

    const payload = (res.data && res.data.data) || {};
    const perfis = Array.isArray(payload.perfis) ? payload.perfis : [];
    commit(
      "SET_CONEXAO_CAIXA_PERFIS",
      perfis.map((p) => ({
        perfil: typeof p === "string" ? p : p.perfil,
        // ausência de `disponivel` é tratada como disponível: o backend só marca o que está tomado.
        disponivel: typeof p === "string" ? true : p.disponivel !== false,
      }))
    );
    return { ok: true };
  },

  async selecionarConexaoCaixaPerfil({ commit, dispatch }, perfil) {
    commit("SET_CONEXAO_CAIXA_VINCULO_LOADING", true);
    commit("SET_CONEXAO_CAIXA_VINCULO_ERRO", null);

    const res = await axiosAuth.post(ROTAS.perfil, { perfil });
    commit("SET_CONEXAO_CAIXA_VINCULO_LOADING", false);

    if (res.error) {
      const erro = extrairErroVinculo(res);
      commit("SET_CONEXAO_CAIXA_VINCULO_ERRO", erro);
      return { ok: false, erro };
    }

    await dispatch("fetchStatusConexaoCaixa");
    await dispatch("fetchConexaoCaixaConfig");
    return { ok: true };
  },

  async gerarConviteConexaoCaixa({ commit }) {
    commit("SET_CONEXAO_CAIXA_VINCULO_LOADING", true);
    commit("SET_CONEXAO_CAIXA_VINCULO_ERRO", null);

    const res = await axiosAuth.post(ROTAS.convite, {});
    commit("SET_CONEXAO_CAIXA_VINCULO_LOADING", false);

    if (res.error) {
      const erro = extrairErroVinculo(res);
      commit("SET_CONEXAO_CAIXA_VINCULO_ERRO", erro);
      return { ok: false, erro };
    }

    const payload = (res.data && res.data.data) || {};
    commit("SET_CONEXAO_CAIXA_CONVITE", {
      codigo: payload.codigo || null,
      expira_em: payload.expira_em || null,
    });
    return { ok: true };
  },

  // O convite é credencial de vida curta: sai da memória assim que o diálogo fecha.
  limparConviteConexaoCaixa({ commit }) {
    commit("SET_CONEXAO_CAIXA_CONVITE", null);
  },

  limparErroVinculoConexaoCaixa({ commit }) {
    commit("SET_CONEXAO_CAIXA_VINCULO_ERRO", null);
  },

  async desconectarConexaoCaixa({ commit, dispatch }) {
    commit("SET_CONEXAO_CAIXA_VINCULO_LOADING", true);

    const res = await axiosAuth.post(ROTAS.desconectar, {});
    commit("SET_CONEXAO_CAIXA_VINCULO_LOADING", false);

    if (res.error) {
      const erro = extrairErroVinculo(res);
      commit("SET_CONEXAO_CAIXA_VINCULO_ERRO", erro);
      return { ok: false, erro };
    }

    commit("SET_CONEXAO_CAIXA_VINCULO", Object.assign({}, VINCULO_VAZIO, { agente: { conectado: false, logado: false, ultimo_contato: null } }));
    commit("SET_CONEXAO_CAIXA_ATIVACAO", null);
    commit("SET_CONEXAO_CAIXA_CONVITE", null);
    commit("SET_CONEXAO_CAIXA_PERFIS", []);
    commit("SET_CONEXAO_CAIXA_VINCULO_ERRO", null);
    await dispatch("fetchConexaoCaixaConfig");
    return { ok: true };
  },

  pararPollingVinculoConexaoCaixa({ commit, state }) {
    clearInterval(state.conexaoCaixaStatusPolling);
    commit("SET_CONEXAO_CAIXA_STATUS_POLLING", null);
  },

  iniciarPollingVinculoConexaoCaixa({ commit, state, dispatch }) {
    // Nunca empilhar: mata o anterior antes de criar o novo.
    clearInterval(state.conexaoCaixaStatusPolling);
    commit("SET_CONEXAO_CAIXA_STATUS_POLLING", null);
    commit("SET_CONEXAO_CAIXA_STATUS_FALHAS", 0);
    commit("SET_CONEXAO_CAIXA_STATUS_DESDE_MS", Date.now());

    // Primeira consulta é imediata — a tela não fica 5s em branco.
    dispatch("fetchStatusConexaoCaixa");

    const intervalo = setInterval(async () => {
      const estado = (state.conexaoCaixaVinculo && state.conexaoCaixaVinculo.estado) || "sem_vinculo";

      if (ESTADOS_QUE_POLLAM.indexOf(estado) === -1) {
        clearInterval(intervalo);
        commit("SET_CONEXAO_CAIXA_STATUS_POLLING", null);
        return;
      }

      if (state.conexaoCaixaStatusDesdeMs
          && Date.now() - state.conexaoCaixaStatusDesdeMs > POLL_TETO_MS) {
        clearInterval(intervalo);
        commit("SET_CONEXAO_CAIXA_STATUS_POLLING", null);
        commit("SET_CONEXAO_CAIXA_VINCULO_ERRO", {
          codigo: "espera_expirada",
          mensagem: "Faz mais de 30 minutos que esta tela está esperando. Clique em \"Verificar agora\" para continuar acompanhando.",
          acao: "verificar",
        });
        return;
      }

      const res = await dispatch("fetchStatusConexaoCaixa");

      if (res && res.ok === false) {
        const falhas = state.conexaoCaixaStatusFalhas + 1;
        commit("SET_CONEXAO_CAIXA_STATUS_FALHAS", falhas);
        if (falhas >= POLL_MAX_FALHAS) {
          clearInterval(intervalo);
          commit("SET_CONEXAO_CAIXA_STATUS_POLLING", null);
        }
      }
    }, POLL_INTERVALO_MS);

    commit("SET_CONEXAO_CAIXA_STATUS_POLLING", intervalo);
  },

  // Devolve `res` para quem precisa distinguir 403 (sem permissao) de falha comum.
  // Nenhum dos chamadores antigos usa o retorno, entao isto e aditivo.
  async fetchConexaoCaixaConfig({ commit }) {
    const res = await axiosAuth.get(ROTAS.config);
    if (res.error) return res;
    commit("SET_CONEXAO_CAIXA_CONFIG", res.data.data || {});
    return res;
  },

  async salvarConexaoCaixaConfig({ commit, dispatch }, payload) {
    commit("SET_CONEXAO_CAIXA_LOADING", true);
    const res = await axiosAuth.post(ROTAS.salvarConfig, payload);
    commit("SET_CONEXAO_CAIXA_LOADING", false);

    if (!res.error) {
      await dispatch("fetchConexaoCaixaConfig");
    }

    return res;
  },

  aguardarLoginSilencioso({ }, jobId) {
    return new Promise(resolve => {
      let tentativas = 0;
      const MAX_TENTATIVAS = 150; // 5 min a 2s/tentativa
      const intervalo = setInterval(async () => {
        if (++tentativas > MAX_TENTATIVAS) {
          clearInterval(intervalo);
          resolve(false);
          return;
        }

        const res = await axiosAuth.get(ROTAS.statusJob(jobId));
        if (res.error) return;

        const { status } = (res.data && res.data.data) || {};

        if (status === 'concluido') {
          clearInterval(intervalo);
          resolve(true);
        } else if (status === 'erro' || status === 'cancelado') {
          clearInterval(intervalo);
          resolve(false);
        }
      }, 2000);
    });
  },

  async iniciarRelatorioTerminalCaixa({ commit, dispatch, state }, { terminal, data, caixa_id }) {
    // Recusa do backend (ex.: sessão anterior aberta) vem explicada no corpo; o
    // texto do axios ("Request failed with status code 400") não diz o que fazer.
    const mensagemDoErro = (erro, padrao) =>
      (erro && erro.response && erro.response.data && erro.response.data.message) ||
      (erro && erro.message) ||
      padrao;

    clearInterval(state.conexaoCaixaRelatorioPolling);
    commit("SET_CONEXAO_CAIXA_RELATORIO_POLLING",   null);
    commit("SET_CONEXAO_CAIXA_RELATORIO_RESULTADO", null);
    commit("SET_CONEXAO_CAIXA_RELATORIO_ERRO",      null);
    commit("SET_CONEXAO_CAIXA_RELATORIO_JOB_ID",    null);
    commit("SET_CONEXAO_CAIXA_RELATORIO_BASE",      null);
    commit("SET_CONEXAO_CAIXA_RELATORIO_CAIXA_ID",  null);
    commit("SET_CONEXAO_CAIXA_RELATORIO_ESTADO",    "iniciando");
    commit("SET_CONEXAO_CAIXA_RELATORIO_LOADING",   true);

    // Preferir caixa_id: o backend resolve terminal e data do próprio caixa
    // (fonte da verdade), evitando enviar nº de terminal desatualizado do front.
    const corpoRelatorio = caixa_id
      ? { caixa_id }
      : { terminal, data: data || new Date().toISOString().split("T")[0] };

    const res = await axiosAuth.post(ROTAS.relatorioTerminal, corpoRelatorio);

    if (res.error) {
      const is400      = res.error.response && res.error.response.status === 400;
      const errData    = is400 && res.error.response.data;
      const payload400 = (errData && errData.data) || errData || {};

      if (is400 && payload400.login_iniciado && payload400.job_id) {
        commit("SET_CONEXAO_CAIXA_MENSAGEM_PROGRESSO",  "Entrando no Painel Parceiros Caixa...");
        commit("SET_CONEXAO_CAIXA_RELATORIO_ESTADO",    "processando");

        const loginOk = await dispatch("aguardarLoginSilencioso", payload400.job_id);

        if (!loginOk) {
          commit("SET_CONEXAO_CAIXA_MENSAGEM_PROGRESSO", "");
          commit("SET_CONEXAO_CAIXA_RELATORIO_LOADING",  false);
          commit("SET_CONEXAO_CAIXA_RELATORIO_ESTADO",   "erro");
          commit("SET_CONEXAO_CAIXA_RELATORIO_ERRO",     "Não foi possível conectar ao Painel Parceiros Caixa.");
          return res;
        }

        commit("SET_CONEXAO_CAIXA_MENSAGEM_PROGRESSO", "Login concluído, buscando dados...");
        commit("SET_CONEXAO_CAIXA_RELATORIO_RESULTADO", null);
        commit("SET_CONEXAO_CAIXA_RELATORIO_ERRO",      null);
        commit("SET_CONEXAO_CAIXA_RELATORIO_JOB_ID",    null);
        commit("SET_CONEXAO_CAIXA_RELATORIO_ESTADO",    "iniciando");

        const resRetry = await axiosAuth.post(ROTAS.relatorioTerminal, corpoRelatorio);

        if (resRetry.error) {
          commit("SET_CONEXAO_CAIXA_MENSAGEM_PROGRESSO", "");
          commit("SET_CONEXAO_CAIXA_RELATORIO_LOADING",  false);
          commit("SET_CONEXAO_CAIXA_RELATORIO_ESTADO",   "erro");
          commit("SET_CONEXAO_CAIXA_RELATORIO_ERRO",     mensagemDoErro(resRetry.error, "Erro ao buscar relatório após login."));
          return resRetry;
        }

        const retryPayload = (resRetry.data && resRetry.data.data) || {};
        commit("SET_CONEXAO_CAIXA_RELATORIO_BASE", retryPayload.base || null);
        commit("SET_CONEXAO_CAIXA_RELATORIO_CAIXA_ID", caixa_id || null);
        const {
          job_id: retryJobId,
          status: retryStatus,
          dados:  retryDados,
          erro:   retryErro,
        } = retryPayload;

        if (retryStatus === "concluido") {
          commit("SET_CONEXAO_CAIXA_RELATORIO_JOB_ID",    retryJobId || null);
          commit("SET_CONEXAO_CAIXA_RELATORIO_RESULTADO", retryDados || null);
          commit("SET_CONEXAO_CAIXA_RELATORIO_ESTADO",    "concluido");
          commit("SET_CONEXAO_CAIXA_RELATORIO_LOADING",   false);
          commit("SET_CONEXAO_CAIXA_MENSAGEM_PROGRESSO",  "");
          return resRetry;
        }

        if (retryStatus === "erro" || retryStatus === "cancelado") {
          commit("SET_CONEXAO_CAIXA_MENSAGEM_PROGRESSO", "");
          commit("SET_CONEXAO_CAIXA_RELATORIO_LOADING",  false);
          commit("SET_CONEXAO_CAIXA_RELATORIO_ESTADO",   "erro");
          commit("SET_CONEXAO_CAIXA_RELATORIO_ERRO",     retryErro || "Falha ao gerar relatório.");
          return resRetry;
        }

        if (!retryJobId) {
          commit("SET_CONEXAO_CAIXA_MENSAGEM_PROGRESSO", "");
          commit("SET_CONEXAO_CAIXA_RELATORIO_LOADING",  false);
          commit("SET_CONEXAO_CAIXA_RELATORIO_ESTADO",   "erro");
          commit("SET_CONEXAO_CAIXA_RELATORIO_ERRO",     "Resposta inválida do servidor.");
          return resRetry;
        }

        commit("SET_CONEXAO_CAIXA_MENSAGEM_PROGRESSO",  "Aguarde, solicitando relatório...");
        commit("SET_CONEXAO_CAIXA_RELATORIO_JOB_ID",    retryJobId);
        commit("SET_CONEXAO_CAIXA_RELATORIO_ESTADO",    "processando");
        dispatch("iniciarPollingRelatorio", retryJobId);
        return resRetry;
      }

      commit("SET_CONEXAO_CAIXA_RELATORIO_LOADING", false);
      commit("SET_CONEXAO_CAIXA_RELATORIO_ESTADO",  "erro");
      commit("SET_CONEXAO_CAIXA_RELATORIO_ERRO",    mensagemDoErro(res.error, "Erro ao iniciar relatório."));
      return res;
    }

    const payload = (res.data && res.data.data) || {};
    // O job pertence ao caixa que pediu: o modal só envia se for o mesmo caixa.
    commit("SET_CONEXAO_CAIXA_RELATORIO_BASE", payload.base || null);
    commit("SET_CONEXAO_CAIXA_RELATORIO_CAIXA_ID", caixa_id || null);
    const { job_id: jobId, status, dados, erro } = payload;

    if (status === "concluido") {
      commit("SET_CONEXAO_CAIXA_RELATORIO_JOB_ID",    jobId || null);
      commit("SET_CONEXAO_CAIXA_RELATORIO_RESULTADO", dados || null);
      commit("SET_CONEXAO_CAIXA_RELATORIO_ESTADO",    "concluido");
      commit("SET_CONEXAO_CAIXA_RELATORIO_LOADING",   false);
      commit("SET_CONEXAO_CAIXA_MENSAGEM_PROGRESSO",  "");
      return res;
    }

    if (status === "erro" || status === "cancelado") {
      commit("SET_CONEXAO_CAIXA_RELATORIO_LOADING",  false);
      commit("SET_CONEXAO_CAIXA_RELATORIO_ESTADO",   "erro");
      commit("SET_CONEXAO_CAIXA_RELATORIO_ERRO",     erro || "Falha ao gerar relatório.");
      commit("SET_CONEXAO_CAIXA_MENSAGEM_PROGRESSO", "");
      return res;
    }

    if (!jobId) {
      commit("SET_CONEXAO_CAIXA_RELATORIO_LOADING", false);
      commit("SET_CONEXAO_CAIXA_RELATORIO_ESTADO",  "erro");
      commit("SET_CONEXAO_CAIXA_RELATORIO_ERRO",    "Resposta inválida do servidor.");
      return res;
    }

    commit("SET_CONEXAO_CAIXA_RELATORIO_JOB_ID", jobId);
    commit("SET_CONEXAO_CAIXA_RELATORIO_ESTADO", "processando");
    dispatch("iniciarPollingRelatorio", jobId);
    return res;
  },

  iniciarPollingRelatorio({ commit, state }, jobId) {
    clearInterval(state.conexaoCaixaRelatorioPolling);
    commit("SET_CONEXAO_CAIXA_RELATORIO_POLLING", null);

    const intervalo = setInterval(async () => {
      const res = await axiosAuth.get(ROTAS.statusJob(jobId));
      if (res.error) return;

      const payload = (res.data && res.data.data) || {};
      const { status, dados, erro, mensagem } = payload;

      if (status === "concluido") {
        clearInterval(intervalo);
        commit("SET_CONEXAO_CAIXA_RELATORIO_POLLING",   null);
        commit("SET_CONEXAO_CAIXA_RELATORIO_RESULTADO", dados || null);
        commit("SET_CONEXAO_CAIXA_RELATORIO_ESTADO",    "concluido");
        commit("SET_CONEXAO_CAIXA_RELATORIO_LOADING",   false);
        commit("SET_CONEXAO_CAIXA_MENSAGEM_PROGRESSO",  "");
        return;
      }

      if (status === "erro" || status === "cancelado") {
        clearInterval(intervalo);
        commit("SET_CONEXAO_CAIXA_RELATORIO_POLLING", null);
        commit("SET_CONEXAO_CAIXA_RELATORIO_ERRO",    erro || "Falha ao gerar relatório.");
        commit("SET_CONEXAO_CAIXA_RELATORIO_ESTADO",  "erro");
        commit("SET_CONEXAO_CAIXA_RELATORIO_LOADING", false);
        commit("SET_CONEXAO_CAIXA_MENSAGEM_PROGRESSO", "");
      }

      if (status === "pendente" || status === "processando") {
        if (mensagem) {
          commit("SET_CONEXAO_CAIXA_MENSAGEM_PROGRESSO", mensagem);
        } else if (state.conexaoCaixaMensagemProgresso === MSG_MANUTENCAO) {
          commit("SET_CONEXAO_CAIXA_MENSAGEM_PROGRESSO", "");
        }
      }
    }, 2000);

    commit("SET_CONEXAO_CAIXA_RELATORIO_POLLING", intervalo);
  },

  pararPollingRelatorio({ commit, state }) {
    clearInterval(state.conexaoCaixaRelatorioPolling);
    commit("SET_CONEXAO_CAIXA_RELATORIO_POLLING",   null);
    commit("SET_CONEXAO_CAIXA_RELATORIO_LOADING",   false);
    commit("SET_CONEXAO_CAIXA_RELATORIO_ESTADO",    "idle");
    commit("SET_CONEXAO_CAIXA_RELATORIO_JOB_ID",    null);
    commit("SET_CONEXAO_CAIXA_RELATORIO_RESULTADO", null);
    commit("SET_CONEXAO_CAIXA_RELATORIO_ERRO",      null);
    commit("SET_CONEXAO_CAIXA_MENSAGEM_PROGRESSO",  "");
  },

  async iniciarListaTerminais({ commit, dispatch, state }, { data }) {
    clearInterval(state.conexaoCaixaTerminaisPolling);
    commit('SET_CONEXAO_CAIXA_TERMINAIS_POLLING',   null);
    commit('SET_CONEXAO_CAIXA_TERMINAIS_JOB_ID',    null);
    commit('SET_CONEXAO_CAIXA_TERMINAIS_RESULTADO', null);
    commit('SET_CONEXAO_CAIXA_TERMINAIS_ERRO',      null);
    commit('SET_CONEXAO_CAIXA_TERMINAIS_ESTADO',    'iniciando');

    const res = await axiosAuth.get(ROTAS.relatorioTerminais, { params: { data } });

    if (res.error) {
      const is400     = res.error.response && res.error.response.status === 400;
      const errData   = is400 && res.error.response.data;
      const payload400 = (errData && errData.data) || errData || {};

      if (is400 && payload400.login_iniciado && payload400.job_id) {
        commit('SET_CONEXAO_CAIXA_MENSAGEM_PROGRESSO',  'Entrando no Painel Parceiros Caixa...');
        commit('SET_CONEXAO_CAIXA_TERMINAIS_ESTADO',    'processando');

        const loginOk = await dispatch('aguardarLoginSilencioso', payload400.job_id);

        if (!loginOk) {
          commit('SET_CONEXAO_CAIXA_MENSAGEM_PROGRESSO', '');
          commit('SET_CONEXAO_CAIXA_TERMINAIS_ESTADO',   'erro');
          commit('SET_CONEXAO_CAIXA_TERMINAIS_ERRO',     'Não foi possível conectar ao Painel Parceiros Caixa.');
          return res;
        }

        commit('SET_CONEXAO_CAIXA_MENSAGEM_PROGRESSO', 'Login concluído, buscando dados...');
        commit('SET_CONEXAO_CAIXA_TERMINAIS_RESULTADO', null);
        commit('SET_CONEXAO_CAIXA_TERMINAIS_ERRO',      null);
        commit('SET_CONEXAO_CAIXA_TERMINAIS_JOB_ID',    null);
        commit('SET_CONEXAO_CAIXA_TERMINAIS_ESTADO',    'iniciando');

        const resRetry = await axiosAuth.get(ROTAS.relatorioTerminais, { params: { data } });

        if (resRetry.error) {
          commit('SET_CONEXAO_CAIXA_MENSAGEM_PROGRESSO', '');
          commit('SET_CONEXAO_CAIXA_TERMINAIS_ESTADO', 'erro');
          commit('SET_CONEXAO_CAIXA_TERMINAIS_ERRO',   (resRetry.error && resRetry.error.message) || 'Erro ao buscar terminais após login.');
          return resRetry;
        }

        const retryPayload = (resRetry.data && resRetry.data.data) || {};
        const {
          job_id: retryJobId,
          status: retryStatus,
          dados:  retryDados,
          erro:   retryErro,
        } = retryPayload;

        if (retryStatus === 'concluido') {
          commit('SET_CONEXAO_CAIXA_TERMINAIS_JOB_ID',    retryJobId || null);
          commit('SET_CONEXAO_CAIXA_TERMINAIS_RESULTADO', retryDados || null);
          commit('SET_CONEXAO_CAIXA_TERMINAIS_ESTADO',    'concluido');
          commit('SET_CONEXAO_CAIXA_MENSAGEM_PROGRESSO',  '');
          return resRetry;
        }

        if (retryStatus === 'erro' || retryStatus === 'cancelado') {
          commit('SET_CONEXAO_CAIXA_MENSAGEM_PROGRESSO', '');
          commit('SET_CONEXAO_CAIXA_TERMINAIS_ESTADO',   'erro');
          commit('SET_CONEXAO_CAIXA_TERMINAIS_ERRO',     retryErro || 'Falha ao listar terminais.');
          return resRetry;
        }

        if (!retryJobId) {
          commit('SET_CONEXAO_CAIXA_MENSAGEM_PROGRESSO', '');
          commit('SET_CONEXAO_CAIXA_TERMINAIS_ESTADO', 'erro');
          commit('SET_CONEXAO_CAIXA_TERMINAIS_ERRO',   'Resposta inválida do servidor.');
          return resRetry;
        }

        commit('SET_CONEXAO_CAIXA_MENSAGEM_PROGRESSO', 'Aguarde, solicitando relatório...');
        commit('SET_CONEXAO_CAIXA_TERMINAIS_JOB_ID',   retryJobId);
        commit('SET_CONEXAO_CAIXA_TERMINAIS_ESTADO',   'processando');
        dispatch('iniciarPollingTerminais', retryJobId);
        return resRetry;
      }

      commit('SET_CONEXAO_CAIXA_TERMINAIS_ESTADO', 'erro');
      commit('SET_CONEXAO_CAIXA_TERMINAIS_ERRO',   (res.error && res.error.message) || 'Erro ao iniciar listagem de terminais.');
      return res;
    }

    const payload = (res.data && res.data.data) || {};
    const { job_id: jobId, status, dados, erro } = payload;

    if (status === 'concluido') {
      commit('SET_CONEXAO_CAIXA_TERMINAIS_JOB_ID',    jobId || null);
      commit('SET_CONEXAO_CAIXA_TERMINAIS_RESULTADO', dados || null);
      commit('SET_CONEXAO_CAIXA_TERMINAIS_ESTADO',    'concluido');
      commit('SET_CONEXAO_CAIXA_MENSAGEM_PROGRESSO',  '');
      return res;
    }

    if (status === 'erro' || status === 'cancelado') {
      commit('SET_CONEXAO_CAIXA_TERMINAIS_ESTADO',   'erro');
      commit('SET_CONEXAO_CAIXA_TERMINAIS_ERRO',     erro || 'Falha ao listar terminais.');
      commit('SET_CONEXAO_CAIXA_MENSAGEM_PROGRESSO', '');
      return res;
    }

    if (!jobId) {
      commit('SET_CONEXAO_CAIXA_TERMINAIS_JOB_ID',  null);
      commit('SET_CONEXAO_CAIXA_TERMINAIS_ESTADO', 'erro');
      commit('SET_CONEXAO_CAIXA_TERMINAIS_ERRO',   'Resposta inválida do servidor.');
      return res;
    }

    commit('SET_CONEXAO_CAIXA_TERMINAIS_JOB_ID', jobId);
    commit('SET_CONEXAO_CAIXA_TERMINAIS_ESTADO', 'processando');
    dispatch('iniciarPollingTerminais', jobId);
    return res;
  },

  iniciarPollingTerminais({ commit, state }, jobId) {
    clearInterval(state.conexaoCaixaTerminaisPolling);
    commit('SET_CONEXAO_CAIXA_TERMINAIS_POLLING', null);

    const intervalo = setInterval(async () => {
      const res = await axiosAuth.get(ROTAS.statusJob(jobId));
      if (res.error) return;

      const payload = (res.data && res.data.data) || {};
      const { status, dados, erro, mensagem } = payload;

      if (status === 'concluido') {
        clearInterval(intervalo);
        commit('SET_CONEXAO_CAIXA_TERMINAIS_POLLING',   null);
        commit('SET_CONEXAO_CAIXA_TERMINAIS_RESULTADO', dados || null);
        commit('SET_CONEXAO_CAIXA_TERMINAIS_ESTADO',    'concluido');
        commit('SET_CONEXAO_CAIXA_MENSAGEM_PROGRESSO',  '');
        return;
      }

      if (status === 'erro' || status === 'cancelado') {
        clearInterval(intervalo);
        commit('SET_CONEXAO_CAIXA_TERMINAIS_POLLING', null);
        commit('SET_CONEXAO_CAIXA_TERMINAIS_ERRO',    erro || 'Falha ao listar terminais.');
        commit('SET_CONEXAO_CAIXA_TERMINAIS_ESTADO',  'erro');
        commit('SET_CONEXAO_CAIXA_MENSAGEM_PROGRESSO', '');
      }

      if (status === 'pendente' || status === 'processando') {
        if (mensagem) {
          commit('SET_CONEXAO_CAIXA_MENSAGEM_PROGRESSO', mensagem);
        } else if (state.conexaoCaixaMensagemProgresso === MSG_MANUTENCAO) {
          commit('SET_CONEXAO_CAIXA_MENSAGEM_PROGRESSO', '');
        }
      }
    }, 2000);

    commit('SET_CONEXAO_CAIXA_TERMINAIS_POLLING', intervalo);
  },

  pararPollingTerminais({ commit, state }) {
    clearInterval(state.conexaoCaixaTerminaisPolling);
    commit('SET_CONEXAO_CAIXA_TERMINAIS_POLLING',   null);
    commit('SET_CONEXAO_CAIXA_TERMINAIS_ESTADO',    'idle');
    commit('SET_CONEXAO_CAIXA_TERMINAIS_JOB_ID',    null);
    commit('SET_CONEXAO_CAIXA_TERMINAIS_RESULTADO', null);
    commit('SET_CONEXAO_CAIXA_TERMINAIS_ERRO',      null);
    commit('SET_CONEXAO_CAIXA_MENSAGEM_PROGRESSO',  '');
  },
};

const APLICATIVO_ID_CONEXAO_CAIXA = 16;

const getters = {
  conexaoCaixaAtivo:            state => state.conexaoCaixaConfig && state.conexaoCaixaConfig.conexao_caixa_ativo === "1",
  conexaoCaixaVinculo:        state => state.conexaoCaixaVinculo,
  conexaoCaixaVinculoEstado:  state => (state.conexaoCaixaVinculo && state.conexaoCaixaVinculo.estado) || "sem_vinculo",
  conexaoCaixaVinculoAtivo:   state => !!state.conexaoCaixaVinculo && state.conexaoCaixaVinculo.estado === "ativo",
  conexaoCaixaAgenteOnline:   state => !!state.conexaoCaixaVinculo
                                    && !!state.conexaoCaixaVinculo.agente
                                    && state.conexaoCaixaVinculo.agente.conectado === true,
  conexaoCaixaAgenteLogado:   state => !!state.conexaoCaixaVinculo
                                    && !!state.conexaoCaixaVinculo.agente
                                    && state.conexaoCaixaVinculo.agente.logado === true,
  conexaoCaixaRelatorioBloqueio: (state, getters) => motivoBloqueioRelatorio(getters),
  conexaoCaixaPodeRelatorio:     (state, getters) => motivoBloqueioRelatorio(getters) === null,
  conexaoCaixaUltimoContato:  state => (state.conexaoCaixaVinculo && state.conexaoCaixaVinculo.agente
                                        && state.conexaoCaixaVinculo.agente.ultimo_contato) || null,
  conexaoCaixaPerfilAtivo:    state => (state.conexaoCaixaVinculo && state.conexaoCaixaVinculo.perfil) || null,
  conexaoCaixaCpfMascarado:   state => (state.conexaoCaixaVinculo && state.conexaoCaixaVinculo.cpf_mascarado) || null,
  conexaoCaixaVinculoErro:    state => state.conexaoCaixaVinculoErro,
  conexaoCaixaVinculoLoading: state => state.conexaoCaixaVinculoLoading,
  conexaoCaixaAtivacao:       state => state.conexaoCaixaAtivacao,
  conexaoCaixaConvite:        state => state.conexaoCaixaConvite,
  conexaoCaixaPerfis:         state => state.conexaoCaixaPerfis,
  conexaoCaixaPerfisLoading:  state => state.conexaoCaixaPerfisLoading,
  conexaoCaixaStatusUltimaConsulta: state => state.conexaoCaixaStatusUltimaConsulta,
  conexaoCaixaPollingAtivo:    state => state.conexaoCaixaStatusPolling !== null,
  conexaoCaixaPollingEsgotado: state => state.conexaoCaixaStatusPolling === null
                                     && state.conexaoCaixaStatusFalhas >= POLL_MAX_FALHAS,
  conexaoCaixaContratado: (state, getters, rootState) => {
    const aplicativos = rootState.profile && rootState.profile.dataEmpresa
      ? rootState.profile.dataEmpresa.aplicativos
      : null;
    if (!Array.isArray(aplicativos)) return false;
    return aplicativos.some(a => Number(a.id) === APLICATIVO_ID_CONEXAO_CAIXA);
  },
  temPermissaoConexaoCaixaGerenciar: (state, getters, rootState) => {
    const acesso = rootState.profile && rootState.profile.dataUser
      ? rootState.profile.dataUser.acesso
      : null;
    return Array.isArray(acesso) && acesso.includes('ConexaoCaixas.gerenciar');
  },
  conexaoCaixaSemAcessoRelatorios: state =>
    state.conexaoCaixaTemAcessoRelatorios === false,
  conexaoCaixaRelatorioProcessando: state => state.conexaoCaixaRelatorioEstado === "processando" || state.conexaoCaixaRelatorioEstado === "iniciando",
  conexaoCaixaRelatorioConcluido:   state => state.conexaoCaixaRelatorioEstado === "concluido",
  conexaoCaixaRelatorioTemErro:     state => state.conexaoCaixaRelatorioEstado === "erro",
  temPermissaoConexaoCaixaRelatorios: (state, getters, rootState) => {
    const acesso = rootState.profile && rootState.profile.dataUser
      ? rootState.profile.dataUser.acesso
      : null;
    return Array.isArray(acesso) && acesso.includes('ConexaoCaixas.relatorios');
  },
  // Base do recurso: a empresa ATIVA é lotérica COM o app 16 contratado
  // (sem permissão). Reutilizável para gatear UI — cada tela combina com a
  // permissão específica que exige (gerenciar e/ou relatórios). Centraliza o
  // antigo `isLoterica && conexaoCaixaContratado` que era duplicado nas views.
  conexaoCaixaAplicavel: (state, getters, rootState) => {
    const empresa  = rootState.profile && rootState.profile.dataEmpresa;
    const segmento = empresa && empresa.assinatura && empresa.assinatura.segmento;
    return segmento === 'Lotéricas' && getters.conexaoCaixaContratado;
  },
  // Fonte única de verdade para HIDRATAR status/config: aplicável + o usuário
  // tem alguma permissão do módulo. Evita requisição desnecessária para
  // assinaturas, lotéricas sem o app, ou usuários sem acesso ao recurso.
  conexaoCaixaDisponivel: (state, getters) =>
    getters.conexaoCaixaAplicavel
    && (getters.temPermissaoConexaoCaixaGerenciar || getters.temPermissaoConexaoCaixaRelatorios),
  conexaoCaixaTerminaisProcessando: state =>
    state.conexaoCaixaTerminaisEstado === 'processando' || state.conexaoCaixaTerminaisEstado === 'iniciando',
  conexaoCaixaTerminaisConcluido:   state => state.conexaoCaixaTerminaisEstado === 'concluido',
  conexaoCaixaTerminaisTemErro:     state => state.conexaoCaixaTerminaisEstado === 'erro',
  conexaoCaixaMensagemProgresso: state => state.conexaoCaixaMensagemProgresso,
  conexaoCaixaConfigCarregada:   state => state.conexaoCaixaConfigCarregada,
};

export default { state, mutations, actions, getters };